// Display-only receipts. Transport authorization continues to use the server's fresh checks.
export const requestIdentity = row => row?.requestId || row?.id;
export const activeRequest = row => ["queued", "running", "unknown"].includes(row?.status);
const stamp = row => Date.parse(row?.updatedAt || row?.approvedAt || row?.requestedAt || "") || 0;
const newer = (a, b) => {
  if (Number.isFinite(a?.revision) && Number.isFinite(b?.revision)) {
    if (a.revision !== b.revision) return a.revision > b.revision;
    if (Number.isFinite(a.progress?.journalRevision) && Number.isFinite(b.progress?.journalRevision))
      return a.progress.journalRevision >= b.progress.journalRevision;
    if (b.progress && !a.progress) return false;
    return true;
  }
  return stamp(a) >= stamp(b);
};

export function mergeRequests(boardRows = [], receipts = []) {
  const rows = new Map(boardRows.map(row => [requestIdentity(row), row]));
  for (const receipt of receipts) {
    const id = requestIdentity(receipt);
    if (!id) continue;
    const old = rows.get(id);
    if (!old || newer(receipt, old)) rows.set(id, { ...old, ...receipt, requestId: id });
  }
  return [...rows.values()];
}

export function withRequestProgress(board, requests = [], complete = false) {
  if (!board || (!requests.length && !complete)) return board;
  const ids = new Set(requests.map(requestIdentity));
  const merged = mergeRequests(complete ? (board.requests || []).filter(row => ids.has(requestIdentity(row))) : board.requests, requests);
  return { ...board, requests: merged, packages: board.packages.map(pkg => {
    const old = new Map((pkg.openRequests || []).filter(row => !complete || ids.has(requestIdentity(row))).map(row => [requestIdentity(row), row]));
    for (const request of merged) {
      if (!(request.items || []).some(item => item.packageId === pkg.packageId)) continue;
      const id = requestIdentity(request);
      if (["pending", "queued", "running", "unknown"].includes(request.status)) old.set(id, {
        ...old.get(id), requestId: id, sourceStage: request.sourceStage, targetStage: request.targetStage,
        status: request.status, targets: request.targets, pinDigest: request.pinDigest,
      });
      else old.delete(id);
    }
    // A receipt may update feedback, never target content or the card's stage.
    const source = pkg.card?.stage;
    const scoped = merged.filter(request => request.sourceStage === source
      && request.items?.some(item => item.packageId === pkg.packageId && item.sourceScopeDigest
        && item.sourceScopeDigest === pkg.cells?.[source]?.scopeDigest))
      .sort((a, b) => (Date.parse(b.requestedAt) || 0) - (Date.parse(a.requestedAt) || 0));
    const receipt = scoped[0], item = receipt?.items?.find(row => row.packageId === pkg.packageId);
    const status = receipt?.status === "failed" && item?.status !== "completed" ? "failed"
      : item?.status === "not_started" && receipt?.status === "partial" ? "failed"
      : ["completed", "failed", "partial", "unknown", "running", "queued"].includes(item?.status) ? item.status : receipt?.status;
    const progressRequest = receipt ? { ...receipt, status,
      execution: item?.execution?.error ? item.execution : receipt.execution,
      progress: item?.progress || receipt.progress } : null;
    return { ...pkg, openRequests: [...old.values()], progressRequest };
  }) };
}

export function mergePreparations(previous = [], next = []) {
  const rows = new Map(previous.map(row => [row.id, row]));
  for (const row of next) if (row?.id && (!rows.has(row.id) || newer(row, rows.get(row.id)))) rows.set(row.id, row);
  return [...rows.values()];
}
